import { FactoryOpts } from 'imask';
import React from 'react';
import { useIMask } from 'react-imask';
import { useDeepCompareMemoize } from 'use-deep-compare-effect';

interface InputMaskProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'children'> {
  maskOptions: FactoryOpts;
  children: (props: object) => React.ReactElement;
  onChange?: any; // yes, that's how it's typed in rc-field-form... I don't have patience to type that crap
}

const InputMaskAlpha: React.FC<InputMaskProps> = ({
  children,
  maskOptions,
  value,
  onChange,
  ...otherProps
}) => {
  // Mask can be an array so we have to deeply compare because references can invoke rerender
  const updatedMask = useDeepCompareMemoize(maskOptions.mask);

  const onAccept = React.useCallback<(val: string) => void>(
    maskedValue => {
      if (onChange) {
        onChange(maskedValue);
      }
    },
    [onChange]
  );

  const {
    ref,
    maskRef,
    value: maskedValue,
    setValue,
    setUnmaskedValue,
  } = useIMask(maskOptions, { onAccept });

  React.useEffect(() => {
    if (typeof value === 'string') {
      setUnmaskedValue(value);
    }
  }, [value]);

  React.useEffect(() => {
    if (maskRef.current) {
      setValue(maskRef.current.value);
      onChange?.(maskRef.current.value as any);
    }
  }, [updatedMask]);

  const childrenProps = {
    inputRef: ref,
    // make sure input receives some value if we have it.
    value: maskedValue ?? value,
    /*
     * FIXME: Silence warning in react - this is because of a bug in
     * text-input.
     */
    // eslint-disable-next-line
    onChange: () => {},
    ...otherProps,
  };

  return children(childrenProps);
};

export const InputMask: React.FC<InputMaskProps> = ({ maskOptions, children, ...rest }) => {
  /*
   * Just use simple control if mask is not provided for some country.
   */
  if (!maskOptions.mask) {
    return children(rest);
  }

  return (
    <InputMaskAlpha maskOptions={maskOptions} {...rest}>
      {children}
    </InputMaskAlpha>
  );
};
